// Tracks screens with work a reload would discard, and holds back app updates while any exist.

const unsavedSources = new Set();
const listeners = new Set();
let isUpdateReady = false;

const notify = () => listeners.forEach((listener) => listener());

/**
 * Records whether a screen currently has work that a page reload would discard.
 * @param {string} source - A unique name for the screen or form holding the work
 * @param {boolean} hasChanges - Whether that screen has unsaved work right now
 * @returns {void}
 * @example
 * setUnsavedChanges("attendance-marking", absentIds.length > 0);
 */
export const setUnsavedChanges = (source, hasChanges) => {
  if (hasChanges) unsavedSources.add(source);
  else unsavedSources.delete(source);
};

/**
 * Checks whether any screen has registered unsaved work.
 * @returns {boolean} True when a reload would discard something
 * @example
 * if (!hasUnsavedChanges()) window.location.reload();
 */
export const hasUnsavedChanges = () => unsavedSources.size > 0;

/**
 * Applies a newly activated service worker. Reloads right away when nothing would be lost,
 * otherwise flags the update so the user can reload when ready.
 * @returns {void}
 * @example
 * navigator.serviceWorker.addEventListener("controllerchange", handleAppUpdate, { once: true });
 */
export const handleAppUpdate = () => {
  if (!hasUnsavedChanges()) {
    window.location.reload();
    return;
  }
  isUpdateReady = true;
  notify();
};

/**
 * Subscribes to the update flag, in the shape `useSyncExternalStore` expects.
 * @param {function} listener - Called whenever the update flag changes
 * @returns {function} Unsubscribes the listener
 * @example
 * const isReady = useSyncExternalStore(subscribeToAppUpdate, getIsAppUpdateReady);
 */
export const subscribeToAppUpdate = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/**
 * Reads whether an update is waiting for the user to reload.
 * @returns {boolean} True when a new version is active but the page was not reloaded
 * @example
 * getIsAppUpdateReady(); // false
 */
export const getIsAppUpdateReady = () => isUpdateReady;
